import React, { useState, useEffect } from 'react';
import { XMarkIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import { syncService } from '../offline_items/syncService';
import { getStoreName } from '../offline_items/endpointMapper';
import ConflictResolutionModal from './ConflictResolutionModal';

const SyncQueueModal = ({ isOpen, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [conflictModalOpen, setConflictModalOpen] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);

  // ##### LOAD ITEMS
  useEffect(() => {
    if (isOpen) {
      loadItems();
    }
  }, [isOpen]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const allItems = await syncService.getAllQueueItems();
      setItems(allItems);
    } catch (error) {
      console.error('Failed to load queue items:', error);
    } finally {
      setLoading(false);
    }
  };

  // ##### SYNC SINGLE ITEM
  const handleSyncItem = async (itemId) => {
    setSyncingId(itemId);
    try {
      await syncService.syncSingleQueueItem(itemId);
      await loadItems();
    } catch (error) {
      console.error('Failed to sync item:', error);
      alert(`Failed to sync item: ${error.message || 'Please try again.'}`);
      await loadItems(); // Reload to show updated status
    } finally {
      setSyncingId(null);
    }
  };

  // ##### DELETE ITEM
  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item from the sync queue?')) {
      return;
    }

    setDeletingId(itemId);
    try {
      await syncService.deleteQueueItem(itemId);
      await loadItems();
    } catch (error) {
      console.error('Failed to delete item:', error);
      alert('Failed to delete item. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  // ##### RETRY FAILED ITEMS
  const handleRetryFailed = async () => {
    setRetrying(true);
    try {
      await syncService.retryFailed();
      await loadItems();
    } catch (error) {
      console.error('Failed to retry failed items:', error);
      alert('Failed to retry failed items. Please try again.');
    } finally {
      setRetrying(false);
    }
  };

  // ##### HANDLE RESOLVE CONFLICT
  const handleResolveConflict = async (itemId, strategy) => {
    try {
      await syncService.resolveConflictWithStrategy(itemId, strategy);
      await loadItems();
      setConflictModalOpen(false);
      setSelectedConflict(null);
    } catch (error) {
      console.error('Failed to resolve conflict:', error);
      throw error;
    }
  };

  // ##### OPEN CONFLICT MODAL
  const handleOpenConflictModal = async (item) => {
    const conflictDetails = await syncService.getConflictDetails(item.id);
    if (conflictDetails) {
      setSelectedConflict({ ...conflictDetails, id: item.id });
      setConflictModalOpen(true);
    }
  };

  // ##### GET FAILED COUNT
  const failedCount = items.filter(item => item.status === 'failed').length;

  // ##### GET ITEM DISPLAY NAME
  const getItemDisplayName = (item) => {
    const storeName = getStoreName(item.url);
    const data = item.data || {};
    
    if (storeName === 'flows') {
      return data.desc || `Flow ${data.id || item.id}`;
    } else if (storeName === 'flow_accounts') {
      return data.account_name || `Account ${data.id || item.id}`;
    } else if (storeName === 'flow_categories') {
      return data.categ_l0 || `Category ${data.id || item.id}`;
    } else if (storeName === 'budgets') {
      return data.description || `Budget ${data.id || item.id}`;
    } else if (storeName === 'health_records') {
      return data.type || `Health Record ${data.id || item.id}`;
    } else if (storeName === 'trips') {
      return data.title || `Trip ${data.id || item.id}`;
    } else if (storeName === 'places') {
      return data.name || `Place ${data.id || item.id}`;
    } else if (storeName === 'tasks') {
      return data.title || `Task ${data.id || item.id}`;
    }
    
    return `Item ${item.id}`;
  };

  // ##### FORMAT TIMESTAMP
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Sync Queue</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No items in queue</div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded ${
                        item.conflict_status === 'detected'
                          ? 'bg-yellow-100 text-yellow-800'
                          : item.status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-800'
                          : item.status === 'retrying'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {item.conflict_status === 'detected' ? 'CONFLICT' : item.status.toUpperCase()}
                      </span>
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                        {item.method}
                      </span>
                    </div>
                    <p className="text-sm font-medium mt-1">{getItemDisplayName(item)}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatTimestamp(item.timestamp)}
                    </p>
                    {item.error && (
                      <p className="text-xs text-red-600 mt-1">{item.error}</p>
                    )}
                  </div>
                  <div className="ml-4 flex items-center gap-2">
                    {item.conflict_status === 'detected' ? (
                      <button
                        onClick={() => handleOpenConflictModal(item)}
                        className="px-3 py-1.5 text-xs bg-yellow-600 hover:bg-yellow-700 text-white rounded"
                        title="Resolve conflict"
                      >
                        Resolve
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleSyncItem(item.id)}
                          disabled={syncingId === item.id || deletingId === item.id}
                          className="p-2 text-gray-800 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Sync this item"
                        >
                          <ArrowPathIcon className={`h-5 w-5 ${syncingId === item.id ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          disabled={deletingId === item.id || syncingId === item.id}
                          className="p-2 text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete this item"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-between items-center">
          {failedCount > 0 && (
            <button
              onClick={handleRetryFailed}
              disabled={retrying}
              className="flex items-center px-4 py-2 rounded-xl bg-gray-800 hover:bg-gray-900 text-white disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              {retrying ? 'Retrying...' : 'Retry Failed Items'}
            </button>
          )}
          <div className={failedCount > 0 ? 'ml-auto' : ''}>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Conflict Resolution Modal */}
      {conflictModalOpen && selectedConflict && (
        <ConflictResolutionModal
          isOpen={conflictModalOpen}
          onClose={() => {
            setConflictModalOpen(false);
            setSelectedConflict(null);
          }}
          conflict={selectedConflict}
          onResolve={handleResolveConflict}
        />
      )}
    </div>
  );
};

export default SyncQueueModal;
